// material-ui
import { Theme } from '@mui/material/styles';
import { Box } from '@mui/material';

// project import
import getColors from 'utils/getColors';

// assets
import { CheckSquareFilled, MinusSquareFilled } from '@ant-design/icons';

// types
import { ExtendedStyleProps } from 'types/extended';

// ==============================|| RADIO - COLORS ||============================== //

function getColorStyle({ color, theme }: ExtendedStyleProps) {
  const colors = getColors(theme, color);
  const { lighter, main, dark } = colors;

  return {
    '&:hover': {
      backgroundColor: lighter,
      '& .icon': {
        borderColor: main
      }
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${dark}`,
      outlineOffset: -4
    }
  };
}

// ==============================|| CHECKBOX - ICON STYLE ||============================== //

// Centers the filled glyph in the bordered box via flexbox (no baseline/absolute-position drift) and
// clips the slight overscan so the colored square fully covers every inner edge. The antd "filled"
// glyph is a centered square ~0.893em wide, so a fontSize of ~1.12x the box just fills it edge to edge.
const iconStyle = (boxSize: number, fontSize: number) => ({
  width: boxSize,
  height: boxSize,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .filled': {
    position: 'static' as const,
    fontSize: `${fontSize}rem`,
    lineHeight: 0
  }
});

// ==============================|| OVERRIDES - CHECKBOX ||============================== //

export default function Checkbox(theme: Theme) {
  const { palette } = theme;

  return {
    MuiCheckbox: {
      defaultProps: {
        className: 'size-small',
        icon: <Box className="icon" sx={{ width: 16, height: 16, border: '1px solid', borderColor: 'inherit', borderRadius: 0.25 }} />,
        checkedIcon: (
          <Box
            className="icon"
            sx={{
              width: 16,
              height: 16,
              border: '1px solid',
              borderColor: 'inherit',
              borderRadius: 0.25,
              position: 'relative'
            }}
          >
            <CheckSquareFilled className="filled" />
          </Box>
        ),
        indeterminateIcon: (
          <Box
            className="icon"
            sx={{
              width: 16,
              height: 16,
              border: '1px solid',
              borderColor: 'inherit',
              borderRadius: 0.25,
              position: 'relative'
            }}
          >
            <MinusSquareFilled className="filled" />
          </Box>
        )
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: 4,
          color: palette.secondary[300],
          // Base icon style applies to every checkbox — including ones (e.g. MUI X DataGrid selection
          // checkboxes) that don't carry a size-* class. Default box is 16px ("small").
          '& .icon': iconStyle(16, 1.15),
          '&.size-medium .icon': iconStyle(20, 1.45),
          '&.size-large .icon': iconStyle(24, 1.72)
        },
        colorPrimary: getColorStyle({ color: 'primary', theme }),
        colorSecondary: getColorStyle({ color: 'secondary', theme }),
        colorSuccess: getColorStyle({ color: 'success', theme }),
        colorWarning: getColorStyle({ color: 'warning', theme }),
        colorInfo: getColorStyle({ color: 'info', theme }),
        colorError: getColorStyle({ color: 'error', theme })
      }
    }
  };
}
